import type { ContentPosition } from '@sunwebgroup/ui-components/uds'
import { useIntl } from '@/intl'
import { Item } from '@/scopes/search/components/QuickSearchBar/Item'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { mqMin, space } from '@sunwebgroup/ui-components'
import { Popover } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

interface FilterProps {
  id: string
  placeholder: string
  title: string
  onConfirm: () => void
  className?: string
  position?: ContentPosition
  additionalSelectedItems?: number
  selectionFormattedText?: string
  compactSelectionFormattedText?: string
  asSheet?: boolean
  hasPadding?: boolean
  ['data-qa']?: string
}

const Content = styled(Popover.Content)`
  height: calc(100vh - ${space('primitives_space_48')}); // This adjust upper spacing of parent sheet
  height: calc(100dvh - ${space('primitives_space_48')}); // using dvh to support for Safari mobile

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: initial;
  }`

function FilterItem({
  id,
  placeholder,
  title,
  children,
  onConfirm,
  additionalSelectedItems,
  selectionFormattedText,
  compactSelectionFormattedText,
  asSheet,
  hasPadding = true,
  'data-qa': dataQa,
  ...rest
}: React.PropsWithChildren<FilterProps>) {
  const intl = useIntl()
  const { activeItemId, setActiveItem } = useQuickSearchStore()
  const isActive = activeItemId === id
  const hasActiveSibling = activeItemId !== null && !isActive

  const toggleActiveState = (open: boolean) => {
    if (open) {
      // Timeout is used as a workaround for focus/blur issues on iOS and Radix UI.
      // Without this delay, opening a new filter can immediately close it due to focus management quirks.
      setTimeout(() => setActiveItem(id), 0)
    }
    else {
      setActiveItem(null)
      onConfirm()
    }
  }

  return (
    <Popover.Root open={isActive} onOpenChange={toggleActiveState}>
      <Popover.Trigger>
        <Item
          placeholder={placeholder}
          title={title}
          isActive={isActive}
          hasActiveSibling={hasActiveSibling}
          additionalSelectedItems={additionalSelectedItems}
          selectionFormattedText={selectionFormattedText}
          data-qa={dataQa}
        />
      </Popover.Trigger>
      <Content hasPadding={hasPadding} title={title} closeText={intl.formatMessage({ id: 'close' })} {...rest}>
        {children}
      </Content>
    </Popover.Root>
  )
}

export const Filter = styled(FilterItem)``
